import { useMemo } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';
import { AlertCircle, TrendingUp, TrendingDown } from 'lucide-react';
import { useI18n } from '@/i18n';
import { Loading } from '@/components/common';
import { useRevenueAnalytics, usePnlSummary } from '@/features/reports/hooks/useReports';
import { StatsCard } from './StatsCard';
import type { RevenueAnalyticsResponse, PnlSummaryResponse } from '@/lib/api/reports';

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function formatCny(cents: number): string {
  return `\u00a5${(cents / 100).toFixed(2)}`;
}

interface RevenueAnalysisProps {
  startDate: string;
  endDate: string;
  excludeTeam?: boolean;
}

export function RevenueAnalysis({ startDate, endDate, excludeTeam }: RevenueAnalysisProps) {
  const { t } = useI18n();
  const { data, isLoading, isError, error } = useRevenueAnalytics({ startDate, endDate, excludeTeam });
  const { data: pnlData, isLoading: pnlLoading, isError: pnlError } = usePnlSummary({
    startDate,
    endDate,
    excludeTeam,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading message={t('reportsPage.loading')} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-semibold text-red-900 mb-1">{t('reportsPage.loadFailed')}</h3>
          <p className="text-sm text-red-700">
            {error instanceof Error ? error.message : t('reportsPage.fetchFailed')}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 text-gray-500">
        {t('reportsPage.revenue.noData')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* P&L Summary - top priority section */}
      {pnlData && pnlData.monthly.length > 0 && <PnlSummarySection data={pnlData} />}
      {pnlData && pnlData.monthly.length === 0 && (
        <div className="bg-white rounded-xl p-5 border border-gray-200 text-center text-gray-500 py-12">
          该时间范围内无交易记录
        </div>
      )}
      {pnlError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 text-sm text-red-700">
          P&L 数据加载失败，请稍后刷新重试
        </div>
      )}
      {pnlLoading && (
        <div className="bg-white rounded-xl p-5 border border-gray-200 animate-pulse h-48" />
      )}

      <OverviewCards data={data} t={t} />
      <DailyRevenueChart data={data} t={t} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChannelPieChart data={data} t={t} />
        <ProductPieChart data={data} t={t} />
      </div>
      <FunnelChart data={data} t={t} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <TrialConversionChart data={data} t={t} />
        <ExpiringUsersCard data={data} t={t} />
        <MigrationsTable data={data} t={t} />
      </div>
      <CohortHeatmap data={data} t={t} />
    </div>
  );
}

interface SectionProps {
  data: RevenueAnalyticsResponse;
  t: (key: string) => string;
}

function OverviewCards({ data, t }: SectionProps) {
  const { overview } = data;
  const netRevenue = overview.total_revenue_cny_cents - overview.refund_cny_cents;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
      <StatsCard
        label={t('reportsPage.revenue.totalRevenue')}
        value={formatCny(overview.total_revenue_cny_cents)}
        variant="profit"
      />
      <StatsCard
        label="退款"
        value={`-${formatCny(overview.refund_cny_cents)}`}
        subtitle={`${overview.refund_count} 笔`}
        variant="danger"
      />
      <StatsCard
        label="净收入"
        value={formatCny(netRevenue)}
        variant="default"
      />
      <StatsCard
        label={t('reportsPage.revenue.transactions')}
        value={overview.total_transactions.toString()}
      />
      <StatsCard
        label={t('reportsPage.revenue.payingUsers')}
        value={overview.paying_users.toString()}
      />
      <StatsCard
        label="活跃订阅"
        value={overview.active_subscriptions.toString()}
        variant="profit"
      />
      <StatsCard
        label="试用中"
        value={overview.trial_users.toString()}
      />
    </div>
  );
}

function DailyRevenueChart({ data, t }: SectionProps) {
  const chartData = useMemo(
    () =>
      data.daily_revenue.map((d) => ({
        date: d.date,
        total: d.total_cny_cents / 100,
        apple: d.apple_cny_cents / 100,
        alipay: d.alipay_cny_cents / 100,
        refund: d.refund_cny_cents / 100,
        net: d.net_cny_cents / 100,
      })),
    [data.daily_revenue]
  );

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.revenue.dailyRevenue')}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `\u00a5${v}`} />
          <Tooltip formatter={(value) => `\u00a5${Number(value).toFixed(2)}`} />
          <Legend />
          <Line type="monotone" dataKey="apple" name="Apple" stroke="#3b82f6" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="alipay" name="Alipay" stroke="#10b981" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="total" name="毛收入" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" dot={false} />
          <Line type="monotone" dataKey="refund" name="退款" stroke="#ef4444" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="net" name="净收入" stroke="#1f2937" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChannelPieChart({ data, t }: SectionProps) {
  const chartData = useMemo(
    () =>
      Object.entries(data.overview.by_channel).map(([name, value]) => ({
        name,
        value: value / 100,
      })),
    [data.overview.by_channel]
  );

  if (chartData.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.revenue.channelDistribution')}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
            {chartData.map((_, i) => (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => `\u00a5${Number(value).toFixed(2)}`} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProductPieChart({ data, t }: SectionProps) {
  const chartData = useMemo(
    () =>
      Object.entries(data.overview.by_product).map(([name, value]) => ({
        name,
        value: value / 100,
      })),
    [data.overview.by_product]
  );

  if (chartData.length === 0) return null;

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-3">{t('reportsPage.revenue.productDistribution')}</h4>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
            {chartData.map((_, i) => (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip formatter={(value) => `\u00a5${Number(value).toFixed(2)}`} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function FunnelChart({ data, t }: SectionProps) {
  const { funnel } = data;
  const steps = useMemo(
    () => [
      { name: t('reportsPage.revenue.funnelRegistered'), value: funnel.registered },
      { name: t('reportsPage.revenue.funnelTrialed'), value: funnel.trialed },
      { name: t('reportsPage.revenue.funnelPurchased'), value: funnel.purchased },
      { name: `${t('reportsPage.revenue.funnelRenewed')} (${t('reportsPage.revenue.renewedAppleOnly')})`, value: funnel.renewed },
      { name: `${t('reportsPage.revenue.funnelChurned')} (${t('reportsPage.revenue.churnedAppleOnly')})`, value: funnel.churned },
    ],
    [funnel, t]
  );

  const maxVal = Math.max(...steps.map((s) => s.value), 1);

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.revenue.conversionFunnel')}</h4>
      <div className="space-y-3">
        {steps.map((step, i) => {
          const pct = maxVal > 0 ? (step.value / maxVal) * 100 : 0;
          const color = i <= 2 ? 'bg-blue-500' : i === 3 ? 'bg-green-500' : 'bg-red-400';
          return (
            <div key={step.name} className="flex items-center gap-3">
              <div className="w-40 text-sm text-gray-600 text-right truncate">{step.name}</div>
              <div className="flex-1 bg-gray-100 rounded-full h-7 relative overflow-hidden">
                <div
                  className={`${color} h-full rounded-full transition-all`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
                <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                  {step.value.toLocaleString()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrialConversionChart({ data, t }: SectionProps) {
  const { trial_conversion_distribution } = data;

  const bestDay = useMemo(() => {
    if (trial_conversion_distribution.length === 0) return null;
    return trial_conversion_distribution.reduce((best, cur) =>
      cur.converted_count > best.converted_count ? cur : best
    );
  }, [trial_conversion_distribution]);

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-2">{t('reportsPage.revenue.trialConversionTiming')}</h4>
      {bestDay && (
        <p className="text-xs text-green-600 mb-3">
          {t('reportsPage.revenue.bestConversionDay').replace('{day}', String(bestDay.day))}
        </p>
      )}
      {trial_conversion_distribution.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('reportsPage.revenue.noData')}</p>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={trial_conversion_distribution}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} label={{ value: 'Day', position: 'insideBottom', offset: -2, fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="converted_count" fill="#3b82f6" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function ExpiringUsersCard({ data, t }: SectionProps) {
  const { expiring_users } = data;
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.revenue.expiringSoon')}</h4>
      <div className="space-y-4">
        {[
          { label: t('reportsPage.revenue.within3Days'), value: expiring_users.within_3_days, color: 'text-red-600 bg-red-50' },
          { label: t('reportsPage.revenue.within7Days'), value: expiring_users.within_7_days, color: 'text-amber-600 bg-amber-50' },
          { label: t('reportsPage.revenue.within14Days'), value: expiring_users.within_14_days, color: 'text-blue-600 bg-blue-50' },
        ].map((item) => (
          <div key={item.label} className={`flex items-center justify-between p-3 rounded-lg ${item.color}`}>
            <span className="text-sm font-medium">{item.label}</span>
            <span className="text-2xl font-bold">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MigrationsTable({ data, t }: SectionProps) {
  const { product_migrations } = data;
  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.revenue.productMigrations')}</h4>
      {product_migrations.length === 0 ? (
        <p className="text-sm text-gray-400 py-8 text-center">{t('reportsPage.revenue.noData')}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-2 py-2 text-left text-gray-500">From</th>
                <th className="px-2 py-2 text-left text-gray-500">To</th>
                <th className="px-2 py-2 text-right text-gray-500">Users</th>
              </tr>
            </thead>
            <tbody>
              {product_migrations.map((m, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="px-2 py-2 text-gray-700">{m.from_product}</td>
                  <td className="px-2 py-2 text-gray-700">{m.to_product}</td>
                  <td className="px-2 py-2 text-right font-medium text-gray-900">{m.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================
// P&L Summary Section
// ============================================================

function formatYuan(cents: number): string {
  return `\u00a5${(cents / 100).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatYuanFromCny(cny: number): string {
  return `\u00a5${cny.toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const PRODUCT_LABELS: Record<string, string> = {
  'com.doratech.dora.pro.monthly': '月卡',
  'com.doratech.dora.pro.yearly': '年卡',
  'dora_pro_monthly': '月卡',
  'dora_pro_yearly': '年卡',
};

function getProductLabel(productId: string): string {
  return PRODUCT_LABELS[productId] || productId;
}

function getChannelLabel(channel: string): string {
  if (channel === 'apple_iap') return 'Apple';
  if (channel === 'alipay') return '支付宝';
  return channel;
}

function getTypeLabel(type: string): string {
  return type === 'renewal' ? '续费' : '新购';
}

function PnlSummarySection({ data }: { data: PnlSummaryResponse }) {
  const { monthly, totals, apple_commission_rate, apple_commission_config: cfg } = data;
  const commissionLabel = cfg
    ? `渠道费 (${(cfg.rate_before * 100).toFixed(0)}% / ${(cfg.rate_after * 100).toFixed(0)}%)`
    : `渠道费 (${(apple_commission_rate * 100).toFixed(0)}%)`;
  const isProfitable = totals.gross_profit_cny >= 0;

  return (
    <div className="space-y-4">
      {/* P&L Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">毛收入</div>
          <div className="text-xl font-bold text-gray-900">{formatYuan(totals.gross_revenue_cents)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">退款</div>
          <div className="text-xl font-bold text-red-500">-{formatYuan(totals.refund_cents)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">{commissionLabel}</div>
          <div className="text-xl font-bold text-red-500">-{formatYuan(totals.apple_commission_cents)}</div>
          {cfg && (
            <div className="text-[10px] text-gray-400 mt-1 leading-tight">
              {cfg.cutover_date} 前 {(cfg.rate_before * 100).toFixed(0)}%，之后 {(cfg.rate_after * 100).toFixed(0)}%
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">净收入</div>
          <div className="text-xl font-bold text-blue-600">{formatYuan(totals.net_revenue_cents)}</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="text-xs text-gray-500 mb-1">LLM 成本</div>
          <div className="text-xl font-bold text-amber-600">-{formatYuanFromCny(totals.llm_cost_cny)}</div>
        </div>
        <div className={`rounded-xl p-4 border ${isProfitable ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
            毛利
            {isProfitable ? <TrendingUp className="w-3 h-3 text-green-600" /> : <TrendingDown className="w-3 h-3 text-red-600" />}
          </div>
          <div className={`text-xl font-bold ${isProfitable ? 'text-green-700' : 'text-red-700'}`}>
            {totals.gross_profit_cny >= 0 ? '+' : ''}{formatYuanFromCny(totals.gross_profit_cny)}
          </div>
        </div>
      </div>

      {/* Monthly P&L Table */}
      <div className="bg-white rounded-xl p-5 border border-gray-200">
        <h4 className="text-sm font-medium text-gray-600 mb-3">月度收支明细</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-gray-500">
                <th className="px-3 py-2 text-left">月份</th>
                <th className="px-3 py-2 text-right">毛收入</th>
                <th className="px-3 py-2 text-right">退款</th>
                <th className="px-3 py-2 text-right">{commissionLabel}</th>
                <th className="px-3 py-2 text-right">净收入</th>
                <th className="px-3 py-2 text-right">LLM成本</th>
                <th className="px-3 py-2 text-right">毛利</th>
              </tr>
            </thead>
            <tbody>
              {monthly.map((m) => {
                const profit = m.gross_profit_cny;
                return (
                  <tr key={m.month} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-900">{m.month}</td>
                    <td className="px-3 py-2 text-right text-gray-900">{formatYuan(m.gross_revenue_cents)}</td>
                    <td className="px-3 py-2 text-right text-red-500">-{formatYuan(m.refund_cents)}</td>
                    <td className="px-3 py-2 text-right text-red-500">-{formatYuan(m.apple_commission_cents)}</td>
                    <td className="px-3 py-2 text-right text-blue-600">{formatYuan(m.net_revenue_cents)}</td>
                    <td className="px-3 py-2 text-right text-amber-600">-{formatYuanFromCny(m.llm_cost_cny)}</td>
                    <td className={`px-3 py-2 text-right font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {profit >= 0 ? '+' : ''}{formatYuanFromCny(profit)}
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr className="border-t-2 border-gray-300 font-bold bg-gray-50">
                <td className="px-3 py-2 text-gray-900">合计</td>
                <td className="px-3 py-2 text-right text-gray-900">{formatYuan(totals.gross_revenue_cents)}</td>
                <td className="px-3 py-2 text-right text-red-500">-{formatYuan(totals.refund_cents)}</td>
                <td className="px-3 py-2 text-right text-red-500">-{formatYuan(totals.apple_commission_cents)}</td>
                <td className="px-3 py-2 text-right text-blue-600">{formatYuan(totals.net_revenue_cents)}</td>
                <td className="px-3 py-2 text-right text-amber-600">-{formatYuanFromCny(totals.llm_cost_cny)}</td>
                <td className={`px-3 py-2 text-right ${isProfitable ? 'text-green-600' : 'text-red-600'}`}>
                  {totals.gross_profit_cny >= 0 ? '+' : ''}{formatYuanFromCny(totals.gross_profit_cny)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Transaction Breakdown Table */}
      {monthly.length > 0 && (
        <div className="bg-white rounded-xl p-5 border border-gray-200">
          <h4 className="text-sm font-medium text-gray-600 mb-3">交易明细</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="px-3 py-2 text-left">月份</th>
                  <th className="px-3 py-2 text-left">类型</th>
                  <th className="px-3 py-2 text-left">产品</th>
                  <th className="px-3 py-2 text-left">渠道</th>
                  <th className="px-3 py-2 text-right">笔数</th>
                  <th className="px-3 py-2 text-right">金额</th>
                </tr>
              </thead>
              <tbody>
                {monthly.flatMap((m) =>
                  m.transactions.map((txn, i) => (
                    <tr key={`${m.month}-${i}`} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900">{i === 0 ? m.month : ''}</td>
                      <td className="px-3 py-2 text-gray-700">{getTypeLabel(txn.type)}</td>
                      <td className="px-3 py-2 text-gray-700">{getProductLabel(txn.product_id)}</td>
                      <td className="px-3 py-2 text-gray-700">{getChannelLabel(txn.channel)}</td>
                      <td className="px-3 py-2 text-right text-gray-900">{txn.count}</td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">{formatYuan(txn.amount_cny_cents)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CohortHeatmap({ data, t }: SectionProps) {
  const { cohort_retention } = data;

  const allMonths = useMemo(() => {
    const set = new Set<number>();
    cohort_retention.forEach((c) => Object.keys(c.retained).forEach((k) => set.add(Number(k))));
    return Array.from(set).sort((a, b) => a - b);
  }, [cohort_retention]);

  if (cohort_retention.length === 0) return null;

  const getColor = (rate: number): string => {
    if (rate >= 80) return 'bg-green-600 text-white';
    if (rate >= 60) return 'bg-green-400 text-white';
    if (rate >= 40) return 'bg-green-300 text-gray-800';
    if (rate >= 20) return 'bg-green-200 text-gray-700';
    if (rate > 0) return 'bg-green-100 text-gray-600';
    return 'bg-gray-50 text-gray-400';
  };

  return (
    <div className="bg-white rounded-xl p-5 border border-gray-200">
      <h4 className="text-sm font-medium text-gray-600 mb-4">{t('reportsPage.revenue.cohortRetention')}</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="px-2 py-1 text-left text-gray-500">Cohort</th>
              <th className="px-2 py-1 text-right text-gray-500">Size</th>
              {allMonths.map((m) => (
                <th key={m} className="px-2 py-1 text-center text-gray-500">M{m}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cohort_retention.map((cohort) => (
              <tr key={cohort.cohort_month} className="border-t border-gray-100">
                <td className="px-2 py-1 text-gray-700 whitespace-nowrap">{cohort.cohort_month}</td>
                <td className="px-2 py-1 text-right text-gray-600">{cohort.cohort_size}</td>
                {allMonths.map((m) => {
                  const count = cohort.retained[m];
                  const rate = count != null && cohort.cohort_size > 0 ? (count / cohort.cohort_size) * 100 : 0;
                  return (
                    <td key={m} className="px-1 py-1 text-center">
                      {count != null ? (
                        <span className={`inline-block w-full rounded px-1 py-0.5 ${getColor(rate)}`}>
                          {rate.toFixed(0)}%
                        </span>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
